
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Libraries from 'plaid-threads/Icons/Libraries';
import Overview from '../../../components/docs/Overview';
import Actions from '../../../components/docs/Actions';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Automate ACATS and reduce rejections with broker-sourced data'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Overview.Header product={'Investments Move'}>
  {metadata.description}
    </Overview.Header>
    <Actions.SmallWrapper miniTitle='Get started with Investments Move'>
  <Actions.SmallCard icon={<Libraries mdxType="Libraries" />} title='API Reference' link='/docs/api/products/investments-move/' />
  
    </Actions.SmallWrapper>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Investments Move (US and CA only) allows you to obtain broker-sourced data to automate brokerage account transfers, reduce operational complexity, and avoid rejections. You can use Investments Move to replace your existing manual ACATS data entry flow or build a new, high-performing flow. Investments Move is designed to increase both the number of users who submit an ACATS transfer and the rate at which these requests successfully settle. `}</p>
    <p>{`Using Investments Move, a user accesses their brokerage account via a Plaid Link-initiated flow, and Plaid returns the source of truth data needed to submit an ACATS transfer, such as account number, account type, account holder name, holdings data, and DTC codes. For brokerages in Canada, Investments Move can retrieve the data necessary for an ATON transfer. `}</p>
    <Callout mdxType="Callout">
      <p>{`Investments Move is currently in early availability and available on a limited basis. To request access, if you are new to Plaid, `}<a parentName="p" {...{
          "href": "https://plaid.com/contact/"
        }}>{`contact sales`}</a>{`; if you are already a Plaid customer, contact your account manager.`}</p>
    </Callout>
    <Callout mdxType="Callout">
      <p>{`Looking for investment holdings or trading activity, without initiating ACATS transfers? See `}<a parentName="p" {...{
          "href": "/docs/investments"
        }}>{`Investments`}</a>{` instead.`}</p>
    </Callout>
    <h4 {...{
      "id": "integration-overview"
    }}>{`Integration overview`}</h4>
    <p>{`The steps below show an overview of how to integrate Investments Move.`}</p>
    <ol>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{`. Along with any other parameters you specify, make sure to include the following:`}
        <ul parentName="li">
          <li parentName="ul">{`The `}<inlineCode parentName="li">{`products`}</inlineCode>{` array should be set to `}<inlineCode parentName="li">{`["investments_auth"]`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`(Optional) To enable `}<a parentName="li" {...{
              "href": "#fallback-flows"
            }}>{`fallback flows`}</a>{`, set the corresponding flag within the `}<inlineCode parentName="li">{`investments_auth`}</inlineCode>{` object. For details, see `}<a parentName="li" {...{
              "href": "#fallback-flows"
            }}>{`Fallback flows`}</a>{`.`}</li>
        </ul>
      </li>
      <li parentName="ol">{`On the client side, create an instance of Link using the `}<inlineCode parentName="li">{`link_token`}</inlineCode>{` returned by `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{`; for more details, see the `}<a parentName="li" {...{
          "href": "/docs/link"
        }}>{`Link documentation`}</a>{`.`}</li>
      <li parentName="ol">{`Once the end user has completed the Link flow, exchange the `}<inlineCode parentName="li">{`public_token`}</inlineCode>{` for an `}<inlineCode parentName="li">{`access_token`}</inlineCode>{` by calling `}<inlineCode parentName="li">{`/item/public_token/exchange`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Call the `}<inlineCode parentName="li">{`/investments/auth/get`}</inlineCode>{` endpoint with the `}<inlineCode parentName="li">{`access_token`}</inlineCode>{` you received from the token exchange. This endpoint will return all the information needed to initiate an ACATS transfer, including account holder name, account number, and DTC number.`}</li>
    </ol>
    <h4 {...{
      "id": "fallback-flows"
    }}>{`Fallback flows`}</h4>
    <p>{`By default, an end user will only be able to select institutions where Plaid can obtain the full account number, account holder name, and holdings data. You can optionally choose to expand the number of institutions available by enabling one or more of the flows below, which allow the end user to directly enter data that Plaid was not able to obtain directly from the broker. `}</p>
    <p>{`To know what data came directly from the brokerage, what was entered by the user and verified against partial data from the brokerage, and what was entered by the user but not verified by the brokerage because no data was available, you can use the `}<inlineCode parentName="p">{`data_sources`}</inlineCode>{` object in the `}<inlineCode parentName="p">{`/investments/auth/get`}</inlineCode>{` response. `}</p>
    <h5 {...{
      "id": "masked-number-match"
    }}>{`Masked Number Match`}</h5>
    <p>{`Masked Number Match supports institutions where Plaid is able to obtain broker-sourced holdings data and account holder information but can only obtain a partial (masked) account number, typically the last four digits of the account number. During the Link flow, the user will be prompted to enter their full account number, which will be verified against the partial account number; if the numbers do not match, the Link attempt will fail and the user will be required to re-enter the account number.`}</p>
    <p>{`To enable Masked Number Match, in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` request, set `}<inlineCode parentName="p">{`investments_auth.masked_number_match_enabled`}</inlineCode>{` to `}<inlineCode parentName="p">{`true`}</inlineCode>{`.`}</p>
    <h5 {...{
      "id": "stated-account-number"
    }}>{`Stated Account Number`}</h5>
    <p>{`Stated Account Number supports institutions where Plaid is able to obtain broker-sourced holdings data, and may or may not be able to obtain account holder information, but cannot obtain any part of an account number.  During the Link flow, the user will be prompted to enter their full account number. If Plaid was unable to obtain account holder information, the user will also be prompted to enter their full name. `}</p>
    <p>{`To enable Stated Account Number, in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` request, set `}<inlineCode parentName="p">{`investments_auth.stated_account_number_enabled`}</inlineCode>{` to `}<inlineCode parentName="p">{`true`}</inlineCode>{`.`}</p>
    <h5 {...{
      "id": "manual-entry"
    }}>{`Manual Entry`}</h5>
    <p>{`Manual Entry supports institutions where Plaid cannot return any information. During the Link flow, users will be able to manually enter the information needed to submit the ACATS transfer. Plaid will still attempt to standardize and enrich the data they provide. `}</p>
    <p>{`To enable Manual Entry, in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` request, set `}<inlineCode parentName="p">{`investments_auth.manual_entry_enabled`}</inlineCode>{` to `}<inlineCode parentName="p">{`true`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "testing-investments-move"
    }}>{`Testing Investments Move`}</h4>
    <p>{`Investments Move can be tested in `}<a parentName="p" {...{
        "href": "/docs/sandbox"
      }}>{`Sandbox`}</a>{` without any additional permissions.`}</p>
    <p>{`To test fallback flows in Sandbox, use the institution Houndstooth Bank (`}<inlineCode parentName="p">{`ins_109512`}</inlineCode>{`). The first fallback flow that is explicitly enabled in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call will be used. The flows will be attempted in the following order: Masked Number Match, Stated Account Number, and Manual Entry. (So for example, if both Masked Number Match and Manual Entry are enabled, Masked Number Match will be used.)`}</p>
    <h4 {...{
      "id": "investments-move-pricing"
    }}>{`Investments Move pricing`}</h4>
    <p>{`Investments Move is billed on a `}<a parentName="p" {...{
        "href": "/docs/account/billing/#per-request-flat-fee"
      }}>{`per-request flat fee model`}</a>{`. To view the exact pricing you may be eligible for, `}<a parentName="p" {...{
        "href": "https://plaid.com/contact/"
      }}>{`contact sales`}</a>{`. `}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "integration-overview",
        level: 4,
        title: "Integration overview",
      },
{
        id: "fallback-flows",
        level: 4,
        title: "Fallback flows",
      },
{
        id: "testing-investments-move",
        level: 4,
        title: "Testing Investments Move",
      },
{
        id: "investments-move-pricing",
        level: 4,
        title: "Investments Move pricing",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
